import { useParams, Link } from 'react-router-dom';
import { useMemo } from 'react';
import SEO from '@/components/feature/SEO';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import VideoCard from '@/components/feature/VideoCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { allVideos } from '@/mocks/videos';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  const video = useMemo(
    () => allVideos.find((v) => v.id === Number(id)),
    [id]
  );

  const relatedVideos = useMemo(() => {
    if (!video) return [];
    return allVideos
      .filter((v) => v.categorySlug === video.categorySlug && v.id !== video.id)
      .slice(0, 6);
  }, [video]);

  if (!video) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-20 pb-14 bg-background-50 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mx-auto mb-4">
              <i className="ri-error-warning-line text-3xl"></i>
            </div>
            <h2 className="text-xl font-bold text-foreground-900 mb-2">视频未找到</h2>
            <p className="text-sm text-foreground-500 mb-6">该视频可能已下架或不存在</p>
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-md hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i> 返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Movie',
    name: video.title,
    description: video.description,
    dateCreated: video.year.toString(),
    director: {
      '@type': 'Person',
      name: video.director,
    },
    actor: video.cast.map((name) => ({
      '@type': 'Person',
      name,
    })),
    countryOfOrigin: video.region,
  };

  return (
    <>
      <ScrollToTop />
      <SEO
        title={`${video.title} - 白云视频高清在线观看 | ${video.year}年${video.region}${video.category}`}
        description={`${video.title}，${video.year}年${video.region}${video.category}，导演${video.director}，主演${video.cast.join('、')}。${video.description.substring(0, 100)}`}
        keywords={`白云视频,${video.title},${video.category},在线观看,免费,${video.tags.join(',')},${video.cast.join(',')},97视频,丝瓜视频`}
        canonicalUrl={`https://www.baiyunvideo.com/article/${video.id}`}
        ogType="video.movie"
        schema={articleSchema}
      />

      <Navbar />

      <main className="pt-20 pb-14 bg-background-50">
        <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
          {/* Breadcrumb */}
          <div className="mb-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: video.category, href: `/category/${video.categorySlug}` },
                { label: video.title },
              ]}
            />
          </div>

          <div className="flex flex-col lg:flex-row gap-8">
            {/* Main Content */}
            <article className="flex-1 min-w-0">
              {/* Video Player Placeholder */}
              <div className="relative w-full aspect-video bg-background-900 rounded-lg overflow-hidden mb-6">
                <img
                  src={video.thumbnail}
                  alt={video.title}
                  title={`${video.title} - 白云视频高清在线观看`}
                  className="w-full h-full object-cover opacity-50"
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-20 h-20 md:w-24 md:h-24 flex items-center justify-center rounded-full bg-primary-500/90 text-background-50 cursor-pointer hover:bg-primary-600 transition-colors">
                    <i className="ri-play-fill text-4xl md:text-5xl ml-1"></i>
                  </div>
                </div>
                <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
                  <span className="text-sm text-background-100/80">{video.duration}</span>
                  <span className="px-2 py-0.5 bg-background-50/20 text-background-50 text-xs rounded">
                    预告片
                  </span>
                </div>
              </div>

              {/* Video Title & Meta */}
              <h1 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mb-4">
                {video.title}
              </h1>

              <div className="flex flex-wrap items-center gap-3 mb-6">
                <span className="flex items-center gap-1 text-sm text-accent-600 font-medium">
                  <i className="ri-star-fill"></i> {video.rating}
                </span>
                <span className="text-sm text-foreground-400">|</span>
                <span className="text-sm text-foreground-600">{video.year}</span>
                <span className="text-sm text-foreground-400">|</span>
                <span className="text-sm text-foreground-600">{video.region}</span>
                <span className="text-sm text-foreground-400">|</span>
                <span className="text-sm text-foreground-600">{video.duration}</span>
                <span className="text-sm text-foreground-400">|</span>
                <span className="text-sm text-foreground-600">{video.views.toLocaleString()} 次播放</span>
              </div>

              {/* Tags */}
              <div className="flex flex-wrap gap-1.5 mb-6">
                {video.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 bg-secondary-100 text-secondary-700 text-xs rounded-full whitespace-nowrap">
                    {tag}
                  </span>
                ))}
              </div>

              {/* Description */}
              <div className="mb-8">
                <h2 className="text-base font-semibold text-foreground-900 mb-3">剧情简介</h2>
                <p className="text-sm text-foreground-700 leading-relaxed">
                  {video.description}
                </p>
              </div>

              {/* Cast & Crew */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-5 bg-background-100 rounded-lg mb-8">
                <div>
                  <span className="text-xs text-foreground-500">导演：</span>
                  <span className="text-sm text-foreground-800 font-medium">{video.director}</span>
                </div>
                <div>
                  <span className="text-xs text-foreground-500">主演：</span>
                  <span className="text-sm text-foreground-800 font-medium">{video.cast.join(' / ')}</span>
                </div>
                <div>
                  <span className="text-xs text-foreground-500">地区：</span>
                  <span className="text-sm text-foreground-800">{video.region}</span>
                </div>
                <div>
                  <span className="text-xs text-foreground-500">更新时间：</span>
                  <span className="text-sm text-foreground-800">{video.updateDate}</span>
                </div>
              </div>
            </article>

            {/* Sidebar - Related Videos */}
            <aside className="w-full lg:w-[320px] flex-shrink-0">
              <h3 className="text-base font-semibold text-foreground-900 mb-4">
                相关推荐
              </h3>
              {relatedVideos.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-1 gap-3">
                  {relatedVideos.map((v) => (
                    <Link
                      key={v.id}
                      to={`/article/${v.id}`}
                      className="flex gap-3 group cursor-pointer"
                    >
                      <div className="w-28 h-16 sm:w-32 sm:h-20 flex-shrink-0 rounded-md overflow-hidden bg-background-200">
                        <img
                          src={v.thumbnail}
                          alt={v.title}
                          title={`${v.title} - 白云视频`}
                          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                          loading="lazy"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-medium text-foreground-800 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors">
                          {v.title}
                        </h4>
                        <div className="flex items-center gap-2 mt-1.5">
                          <span className="text-xs text-accent-600">
                            <i className="ri-star-fill text-xs mr-0.5"></i>{v.rating}
                          </span>
                          <span className="text-xs text-foreground-400">{v.year}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-foreground-400">暂无相关推荐</p>
              )}
            </aside>
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}